/**
 * CameraCapture — live-camera modal. Stream/permission logic lives in
 * useCameraStream; failure UIs live in CameraRecovery. This component only
 * renders the modal chrome, the live preview and the capture controls.
 */
import { useEffect, useRef, useState } from "react";
import { X, Camera as CameraIcon, ArrowsClockwise, Check } from "@phosphor-icons/react";
import { useCameraStream } from "@/components/camera/useCameraStream";
import { CameraRecovery } from "@/components/camera/CameraRecovery";

export default function CameraCapture({ open, onClose, onCapture, maxPhotos = 6, currentCount = 0 }) {
  const canvasRef = useRef(null);
  const [taken, setTaken] = useState(0);
  const { videoRef, phase, errMsg, devices, flip, retry } = useCameraStream(open);

  useEffect(() => {
    if (!open) setTaken(0);
  }, [open]);

  if (!open) return null;

  const takePhoto = () => {
    const video = videoRef.current;
    const canvas = canvasRef.current;
    if (!video || !canvas) return;
    const w = video.videoWidth || 1280;
    const h = video.videoHeight || 720;
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext("2d");
    ctx.drawImage(video, 0, 0, w, h);
    const dataUrl = canvas.toDataURL("image/jpeg", 0.9);
    const b64 = dataUrl.split(",")[1];
    onCapture(b64);
    setTaken((n) => n + 1);
  };

  const openInNewTab = () => {
    try {
      window.open(window.location.href, "_blank", "noopener,noreferrer");
    } catch {
      /* popup blocker — user can copy URL */
    }
  };

  const remaining = Math.max(0, maxPhotos - currentCount - taken);
  const full = remaining === 0;

  return (
    <div
      className="fixed inset-0 z-[9998] bg-black/90 flex items-center justify-center p-2 sm:p-4"
      data-testid="camera-modal"
    >
      <div
        className="relative w-full max-w-3xl bg-black border border-zinc-800 flex flex-col"
        style={{ maxHeight: "95vh" }}
      >
        <div className="flex items-center justify-between px-4 py-3 border-b border-zinc-800 text-white">
          <div>
            <div className="overline text-white/60">Cámara</div>
            <div className="text-sm font-medium">
              {taken > 0
                ? `${taken} foto${taken === 1 ? "" : "s"} tomada${taken === 1 ? "" : "s"}`
                : phase === "live"
                ? "Toma una foto"
                : phase === "requesting"
                ? "Pidiendo permiso…"
                : "Acceso a cámara"}
            </div>
          </div>
          <div className="flex items-center gap-2">
            {phase === "live" && devices.length > 1 && (
              <button
                type="button"
                onClick={flip}
                className="p-2 text-white/80 hover:bg-white/10"
                data-testid="camera-flip-btn"
                title="Cambiar cámara"
              >
                <ArrowsClockwise size={20} />
              </button>
            )}
            <button
              type="button"
              onClick={onClose}
              className="p-2 text-white/80 hover:bg-white/10"
              data-testid="camera-close-btn"
              title="Cerrar"
            >
              <X size={20} />
            </button>
          </div>
        </div>

        <div
          className="relative flex-1 bg-black flex items-center justify-center"
          style={{ minHeight: 360 }}
        >
          {/* Live preview (always mounted so videoRef attaches immediately) */}
          <video
            ref={videoRef}
            playsInline
            muted
            autoPlay
            className={`w-full h-auto max-h-[72vh] object-contain ${
              phase === "live" ? "" : "hidden"
            }`}
            data-testid="camera-video"
          />

          {phase === "requesting" && (
            <div className="absolute inset-0 flex flex-col items-center justify-center text-white/80 gap-3 p-6 text-center">
              <div className="w-10 h-10 border-2 border-white/60 border-t-transparent rounded-full animate-spin" />
              <div className="text-sm font-medium">Esperando permiso del navegador…</div>
              <div className="text-xs text-white/60 max-w-md">
                Verás un popup arriba pidiéndote autorizar la cámara. Haz clic en <strong>Permitir</strong>.
              </div>
            </div>
          )}

          <CameraRecovery
            phase={phase}
            errMsg={errMsg}
            onRetry={retry}
            onOpenNewTab={openInNewTab}
            onClose={onClose}
          />

          <canvas ref={canvasRef} className="hidden" />
        </div>

        {phase === "live" && (
          <div className="px-4 py-4 border-t border-zinc-800 flex items-center justify-between gap-3">
            <div className="text-xs text-white/60">
              {full
                ? "Límite alcanzado"
                : `Quedan ${remaining} foto${remaining === 1 ? "" : "s"} disponibles`}
            </div>
            <div className="flex items-center gap-2">
              {taken > 0 && (
                <button
                  type="button"
                  onClick={onClose}
                  className="px-4 py-2 text-sm border border-white/20 text-white hover:bg-white/10 inline-flex items-center gap-2"
                  data-testid="camera-done-btn"
                >
                  <Check size={16} /> Listo
                </button>
              )}
              <button
                type="button"
                onClick={takePhoto}
                disabled={full}
                className="bg-klein text-white px-5 py-2.5 text-sm font-medium hover:bg-klein/90 disabled:opacity-40 disabled:cursor-not-allowed inline-flex items-center gap-2"
                data-testid="camera-shoot-btn"
              >
                <CameraIcon size={18} weight="fill" /> Capturar
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
